<script setup lang="ts">
  import { VRadio } from "@krainovsd/vue-ui";
  import { computed, ref } from "vue";
  import { TARGET_OPTIONS } from "@/entities/comparison";
  import type { ComparisonMode, ComparisonTarget } from "@/entities/comparison";
  import Card from "@/components/atoms/Card.vue";
  import StepLayout from "@/components/molecules/constructor/StepLayout.vue";

  const target = defineModel<ComparisonTarget>("target", { required: true });
  const mode = defineModel<ComparisonMode>("mode", { required: true });
  const emit = defineEmits<{ next: [] }>();

  const layoutRef = ref<{ rootEl: HTMLElement | null } | null>(null);
  defineExpose({
    rootEl: computed(() => layoutRef.value?.rootEl ?? null),
  });

  const MODE_OPTIONS: { value: ComparisonMode; title: string; description: string }[] = [
    {
      value: "previous_period",
      title: "С прошлым периодом",
      description: "Факторный анализ дельт по разрезам.",
    },
    {
      value: "anotherTarget",
      title: "С другим объектом",
      description: "Факторный или байесовский анализ.",
    },
  ];
</script>

<template>
  <StepLayout
    ref="layoutRef"
    :title="$t('page.createComparison.step1.title', {}, { default: 'Что сравниваем' })"
    next
    @next="emit('next')"
  >
    <div :class="$style.block">
      <span :class="$style.blockTitle">
        {{ $t("page.createComparison.step1.targetBlock", {}, { default: "Сравнить" }) }}
      </span>
      <div :class="$style.chips">
        <div
          v-for="option in TARGET_OPTIONS"
          :key="option.value"
          :class="[$style.chip, target === option.value && $style.chipActive]"
          @click="target = option.value"
        >
          {{ option.label }}
        </div>
      </div>
    </div>

    <div :class="$style.block">
      <span :class="$style.blockTitle">
        {{ $t("page.createComparison.step1.modeBlock", {}, { default: "Режим" }) }}
      </span>
      <div :class="$style.modes">
        <Card
          v-for="option in MODE_OPTIONS"
          :key="option.value"
          :class="$style.modeCard"
          @click="mode = option.value"
        >
          <VRadio
            v-model="mode"
            :name="option.title"
            :value="option.value"
            :class="$style.modeRadio"
          />
          <div :class="$style.modeContent">
            <span :class="$style.modeTitle">{{ option.title }}</span>
            <span :class="$style.modeDescription">{{ option.description }}</span>
          </div>
        </Card>
      </div>
    </div>
  </StepLayout>
</template>

<style lang="scss" module>
  .block {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .blockTitle {
    color: var(--foreground);
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .chip {
    padding: 0.625rem 1rem;

    border: 1px solid var(--border);
    border-radius: 0.875rem;

    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.25rem;

    color: var(--foreground);
    cursor: pointer;
  }

  .chipActive {
    border-color: var(--brand);
  }

  .modes {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .modeCard {
    padding: 14px;

    display: flex;
    align-items: flex-start;

    cursor: pointer;
  }

  .modeRadio {
    margin-top: 2px;
  }

  .modeContent {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .modeTitle {
    color: var(--foreground);
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .modeDescription {
    color: var(--muted-foreground);
    font-size: 13px;
    line-height: 1.375;
  }
</style>
